import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authFetch } from "../../lib/api";

// ─── Thunks ────────────────────────────────────────────────────────────────

export const fetchProjectReport = createAsyncThunk(
  "projectReport/fetch",
  async (
    { projectId, tab = "status", page = 1, limit = 5 },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams({ tab, page, limit });
      const res = await authFetch(
        `/api/reports/project/${projectId}?${params}`,
      );
      const json = await res.json();
      if (!res.ok)
        return rejectWithValue(
          json?.message || "Failed to fetch project report",
        );
      return { ...json.data, projectId };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const fetchReportsMain = createAsyncThunk(
  "projectReport/fetchMain",
  async (_, { rejectWithValue }) => {
    try {
      const res = await authFetch("/api/reports/summary");
      const json = await res.json();
      if (!res.ok)
        return rejectWithValue(
          json?.message || "Failed to fetch reports summary",
        );
      return json.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

// ─── Slice ─────────────────────────────────────────────────────────────────

const projectReportSlice = createSlice({
  name: "projectReport",
  initialState: {
    // main page
    mainSummary: null,
    isLoadingMain: false,
    mainError: null,

    // project report page
    data: null,
    isLoading: false,
    error: null,
    selectedProjectId: null,
    activeTab: "Status", // "Status" | "Timesheet" | "Risk"
    page: 1,
  },
  reducers: {
    setSelectedProjectId(state, action) {
      state.selectedProjectId = action.payload;
      state.data = null;
      state.page = 1;
      state.activeTab = "Status";
    },
    setActiveTab(state, action) {
      state.activeTab = action.payload;
      state.page = 1;
    },
    setProjectPage(state, action) {
      state.page = action.payload;
    },
    clearProjectReport(state) {
      state.data = null;
      state.error = null;
      state.selectedProjectId = null;
      state.page = 1;
      state.activeTab = "Status";
    },
  },
  extraReducers: (builder) => {
    builder
      // main summary
      .addCase(fetchReportsMain.pending, (state) => {
        state.isLoadingMain = true;
        state.mainError = null;
      })
      .addCase(fetchReportsMain.fulfilled, (state, action) => {
        state.isLoadingMain = false;
        state.mainSummary = action.payload;
      })
      .addCase(fetchReportsMain.rejected, (state, action) => {
        state.isLoadingMain = false;
        state.mainError = action.payload || "Unknown error";
      })
      // project detail
      .addCase(fetchProjectReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProjectReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(fetchProjectReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Unknown error";
      });
  },
});

export const {
  setSelectedProjectId,
  setActiveTab,
  setProjectPage,
  clearProjectReport,
} = projectReportSlice.actions;

// ─── Selectors ─────────────────────────────────────────────────────────────

export const selectMainSummary = (s) => s.projectReport.mainSummary;
export const selectIsLoadingMain = (s) => s.projectReport.isLoadingMain;
export const selectProjectReport = (s) => s.projectReport.data;
export const selectIsLoadingProject = (s) => s.projectReport.isLoading;
export const selectProjectError = (s) => s.projectReport.error;
export const selectSelectedProjectId = (s) => s.projectReport.selectedProjectId;
export const selectActiveTab = (s) => s.projectReport.activeTab;
export const selectProjectPage = (s) => s.projectReport.page;

export default projectReportSlice.reducer;
